import React, { useState } from 'react';
import { useCompany } from '@/context/CompanyContext';
import { useInputValidation } from '@/hooks/useInputValidation';
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const CompanySetup: React.FC = () => {
  const { createCompany, loading } = useCompany();
  const { validateCompanyName, sanitizeInput } = useInputValidation();
  const { logSecurityEvent } = useSecurityMonitoring();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeInput(e.target.value);
    setName(sanitized);
    
    if (validationError && sanitized.trim()) {
      setValidationError('');
    }
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const sanitized = sanitizeInput(e.target.value);
    setDescription(sanitized);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validation = await validateCompanyName(name);
    if (!validation.isValid) {
      setValidationError(validation.error || 'Nom d\'entreprise invalide');
      await logSecurityEvent('input_validation_failed', {
        field: 'company_name',
        error: 'Invalid company name format'
      });
      return;
    }

    setIsCreating(true);
    setValidationError('');
    
    try {
      const success = await createCompany(name.trim(), description.trim() || undefined);
      
      if (success) {
        setName('');
        setDescription('');
        await logSecurityEvent('company_created', {
          company_name: name.trim()
        });
      }
    } catch (error) {
      setValidationError('Une erreur est survenue lors de la création de l\'entreprise');
      await logSecurityEvent('company_creation_failed', {
        error: 'Company creation failed'
      });
    } finally {
      setIsCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span>Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl">Bienvenue !</CardTitle>
          <p className="text-muted-foreground">
            Créez votre entreprise pour commencer à gérer votre inventaire
          </p>
        </CardHeader>
        <CardContent>
          {validationError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de l'entreprise *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Mon Entreprise"
                value={name}
                onChange={handleNameChange}
                required
                disabled={isCreating}
                maxLength={100}
                autoComplete="organization"
              />
              <p className="text-xs text-muted-foreground">
                2-100 caractères, uniquement des lettres, chiffres et espaces
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description (optionnel)</Label>
              <Textarea
                id="description"
                placeholder="Description de votre entreprise..."
                value={description}
                onChange={handleDescriptionChange}
                disabled={isCreating}
                rows={3}
                maxLength={500}
              />
              <p className="text-xs text-muted-foreground">
                Maximum 500 caractères
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full" 
              disabled={!name.trim() || isCreating || !!validationError}
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Création...
                </>
              ) : (
                <>
                  <Building2 className="w-4 h-4 mr-2" />
                  Créer mon entreprise
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};